import { SurfaceDropComponent, SurfaceDropProps, SurfaceDropState } from "@jsplumbtoolkit/browser-ui-react";
import { NodeType, QuestInterface, QuestType } from "@swivl-mono/types";
import { useEffect, useMemo } from "react";
import { AiOutlineAlert, AiOutlineCloudServer, AiOutlineContacts, AiOutlineFlag } from "react-icons/ai";
import { BiBrain, BiCarousel, BiMessageRoundedDetail, BiNote, BiTimeFive, BiUserVoice } from "react-icons/bi";
import { BsBracesAsterisk, BsDatabaseFillDown, BsKeyboard, BsPersonPlus } from "react-icons/bs";
import { DiCode } from "react-icons/di";
import { FaFileContract, FaHubspot, FaRegHandPointDown, FaWarehouse } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import { GiLogicGateNot, GiPegasus } from "react-icons/gi";
import { GoFileMedia } from "react-icons/go";
import { GrTableAdd } from "react-icons/gr";
import { HiBeaker, HiOutlineBeaker, HiOutlineMail } from "react-icons/hi";
import { ImProfile } from "react-icons/im";
import { IoIosGlobe, IoIosLink, IoMdAnalytics } from "react-icons/io";
import { CiCreditCard2 } from "react-icons/ci";
import { IoPhonePortraitOutline, IoSaveOutline, IoStorefrontOutline } from "react-icons/io5";
import { LuChevronsUpDown } from "react-icons/lu";
import { MdDataArray, MdViewArray } from "react-icons/md";
import { RiNodeTree, RiSlackLine } from "react-icons/ri";
import { TbAuth2Fa, TbRobotFace, TbShieldCheck } from "react-icons/tb";
import { TiMessageTyping } from "react-icons/ti";
import { VscDebugContinue } from "react-icons/vsc";

import FMSModel from "../../../../../Models/FMS/FMS.Model";

interface NodeItemInterface {
  type: string;
  title: string;
  icon: any;
  condition: "all" | "router" | "quest" | "storeFinder" | "storage";
}

const allNodes: NodeItemInterface[] = [
  { title: "Array", type: "array", condition: "all", icon: <MdDataArray /> },
  { title: "Array Picker", type: NodeType.ArrayPicker, condition: "quest", icon: <MdViewArray /> },
  { title: "Analytics", type: "analytics", condition: "all", icon: <IoMdAnalytics /> },
  { title: "API Request", type: "action", condition: "all", icon: <AiOutlineCloudServer /> },
  { title: "Buttons", type: "buttons", condition: "quest", icon: <FaRegHandPointDown /> },
  { title: "Carousel", type: "carousel", condition: "quest", icon: <BiCarousel /> },
  { title: "Command", type: "command", condition: "all", icon: <DiCode /> },
  { title: "Continue", type: "continue", condition: "router", icon: <VscDebugContinue /> },
  { title: "CRM", type: "crm", condition: "all", icon: <AiOutlineContacts /> },
  { title: "Date & Time", type: "dateAndTime", condition: "quest", icon: <BiTimeFive /> },
  { title: "Email", type: "email", condition: "quest", icon: <HiOutlineMail /> },
  { title: "Escalation", type: "escalation", condition: "all", icon: <AiOutlineAlert /> },
  { title: "Exp. End", type: "experimentEnd", condition: "all", icon: <HiBeaker /> },
  { title: "Exp. Start", type: "experiment", condition: "all", icon: <HiOutlineBeaker /> },
  { title: "Finish", type: "finish", condition: "quest", icon: <AiOutlineFlag /> },
  { title: "Hubspot", type: "hubspot", condition: "quest", icon: <FaHubspot /> },
  { title: "Increment", type: "increment", condition: "quest", icon: <LuChevronsUpDown /> },

  { title: "Input", type: "input", condition: "quest", icon: <BsKeyboard /> },
  { title: "Lease Picker", type: NodeType.LeasePicker, condition: "all", icon: <FaFileContract /> },

  { title: "LLM", type: "llm", condition: "quest", icon: <TbRobotFace /> },
  { title: "Load Data", type: NodeType.LoadData, condition: "all", icon: <BsDatabaseFillDown /> },

  { title: "Logic", type: "logic", condition: "quest", icon: <GiLogicGateNot /> },
  { title: "Media", type: "media", condition: "quest", icon: <GoFileMedia /> },
  { title: "Message", type: "message", condition: "quest", icon: <BiMessageRoundedDetail /> },
  { title: "Multi-Select", type: "multiSelect", condition: "quest", icon: <BiMessageRoundedDetail /> },
  { title: "NLP", type: "nlp", condition: "quest", icon: <BiBrain /> },
  { title: "Note", type: NodeType.Note, condition: "all", icon: <BiNote /> },
  { title: "Notification", type: "notification", condition: "all", icon: <BiMessageRoundedDetail /> },
  { title: "Open or Close", type: "openOrClose", condition: "all", icon: <BiMessageRoundedDetail /> },
  { title: "Past Messages", type: "pastMessages", condition: "all", icon: <BiMessageRoundedDetail /> },
  { title: "Profile Collect", type: "profileCollect", condition: "quest", icon: <ImProfile /> },

  { title: "Quest", type: "quest", condition: "all", icon: <GiPegasus /> },
  { title: "Random", type: "random", condition: "quest", icon: <BiMessageRoundedDetail /> },
  { title: "Route", type: "route", condition: "router", icon: <RiNodeTree /> },
  { title: "Save", type: "save", condition: "all", icon: <IoSaveOutline /> },
  { title: "Short URL", type: NodeType.ShortURL, condition: "quest", icon: <IoIosLink /> },
  { title: "Payment Link", type: NodeType.PaymentLink, condition: "quest", icon: <CiCreditCard2 /> },
  { title: "Slack", type: "slack", condition: "quest", icon: <RiSlackLine /> },
  {
    title: "SMS Group",
    type: "SMSGroup",
    condition: "all",
    icon: (
      <i className="iconRow">
        <IoPhonePortraitOutline />
        <FiUsers />
      </i>
    ),
  },
  { title: "SMS", type: "SMS", condition: "quest", icon: <IoPhonePortraitOutline /> },
  { title: "Space Picker", type: "spacePicker", condition: "storage", icon: <FaWarehouse /> },
  { title: "Store Finder", type: "storeFinderLookup", condition: "storeFinder", icon: <IoStorefrontOutline /> },
  {
    title: "Store Lead",
    type: "storeLead",
    condition: "storage",
    icon: (
      <i className="iconRow">
        <BsPersonPlus />
        <IoStorefrontOutline />
      </i>
    ),
  },
  {
    title: "Store Tenant",
    type: "storeGetTenant",
    condition: "storage",
    icon: (
      <i className="iconRow">
        <BsPersonPlus />
      </i>
    ),
  },
  {
    title: "Store Access",
    type: "storeAccessCodes",
    condition: "storage",
    icon: (
      <i className="iconRow">
        <BsBracesAsterisk />
      </i>
    ),
  },
  { title: "Table Save", type: "tableSaveRow", condition: "all", icon: <GrTableAdd /> },
  { title: "Typing", type: "typingIndicator", condition: "quest", icon: <TiMessageTyping /> },
  { title: "Validation", type: "userValidation", condition: "all", icon: <TbAuth2Fa /> },


  { title: "URL", type: "url", condition: "quest", icon: <IoIosGlobe /> },
  { title: "Validate & Normalize", type: "validateNormalize", condition: "all", icon: <TbShieldCheck /> },
  { title: "Voice", type: "voice", condition: "all", icon: <BiUserVoice /> },
];

interface Props extends SurfaceDropProps {
  quest: QuestInterface;
  searchQuery: string;
}

export default function NodePaletteNodes(props: Props) {
  const { quest } = props;
  const { FMSs } = FMSModel.useState();

  useEffect(() => {
    FMSModel.load(false);
  }, []);

  const possibileNodes = useMemo(() => {
    if (!quest) return [];

    let questType = "quest";
    if (quest.type === QuestType.ROUTER) {
      questType = "router";
    }

    let showStoreFinder = true;
    let showStorage = true; // ( FMSs && FMSs.length) ||  (bot.get("apps")  && (bot.get("apps").includes("siteLink") || bot.get("apps").includes("tenant") || bot.get("apps").includes("ssm") ||  bot.get("apps").includes("storEdge")) ? true : false)

    return allNodes.filter((node) => {
      if (node.condition === "all") {
        return true;
      }
      if (node.condition === questType) {
        return true;
      }
      if (node.condition === "storeFinder" && showStoreFinder) {
        return true;
      }
      if (node.condition === "storage" && showStorage) {
        return true;
      }
      return false;
    });
  }, [quest, FMSs]);
  const internalProps = { ...props, possibileNodes: possibileNodes };
  return (
    <InternalNodePaletteNodes
      {...internalProps}
      // allowDropOnCanvas={true}
      allowDropOnEdge={"false"}
      allowDropOnGroup={"false"}
    />
  );
}

class InternalNodePaletteNodes extends SurfaceDropComponent {
  render() {
    const { searchQuery, possibileNodes } = this.props as unknown as any;

    let items = searchQuery.length
      ? possibileNodes.filter((item: any) => item.title.toLowerCase().includes(searchQuery.toLowerCase()))
      : possibileNodes;

    return items.map((item: any) => {
      return (
        <div className="nodePaletteItem" key={item.type} data-node-type={item.type}>
          {item.icon}
          <span>{item.title}</span>
        </div>
      );
    });

    // return [
    //     <div key="message" data-node-type="message" title="Drag to add new Message">
    //         <BiMessageRoundedDetail />
    //         <span>Message</span>
    //     </div>,
    //     <div key="buttons" data-node-type="buttons" title="Drag to add new Buttons">
    //         <FaRegHandPointDown/>
    //         <span>Buttons</span>
    //     </div>
    // ];
  }
}
